import { useState } from "react"
import Select from "react-select"
import { useDebounceState } from "src/hooks/useDebounce"
import { useUsersListSWR } from "src/hooks/useUsersListSWR"
import { User, useUserSWR } from "src/hooks/useUserSWR"

type Option = { value: string; label: string }

type Props = {
  /** FormField が label と結び付けるために渡す id */
  id?: string
  value: string
  onChange: (value: string) => void
  error?: boolean
}

const SEARCH_DEBOUNCE_MS = 300
const SEARCH_LIMIT = 30

const toOption = (user: Pick<User, "id" | "lastFurigana" | "firstFurigana">): Option => {
  const furigana = [user.lastFurigana, user.firstFurigana].filter(Boolean).join(" ")
  return {
    value: user.id.toString(),
    label: `${furigana || "（フリガナ未設定）"}（ID: ${user.id}）`,
  }
}

/**
 * 受電ログに紐付けるユーザーを選ぶ。ユーザーは数が多く全件は読めないので、
 * 入力した名前・フリガナ・ID・電話番号でユーザー一覧と同じ検索をかけて候補を出す。未選択は空文字
 */
export const IncomingCallLogUserSelect: React.FC<Props> = ({ id, value, onChange, error }) => {
  const [inputValue, setInputValue] = useState("")
  // 選んだ後は検索文字が消えて候補も空になるので、選んだ候補を覚えておいて表示に使う
  const [pickedOption, setPickedOption] = useState<Option | null>(null)
  const searchParams = useDebounceState(inputValue.trim(), SEARCH_DEBOUNCE_MS)
  const { users, isLoading } = useUsersListSWR({
    searchParams,
    limit: SEARCH_LIMIT,
    enabled: searchParams !== "",
  })
  // ユーザー一覧から開いたときなど、検索結果にない選択中のユーザーも名前を出せるように取得する
  const { data: selectedUser } = useUserSWR(Number(value) || 0)

  const options = users.map(toOption)
  const selectedOption =
    value === ""
      ? null
      : pickedOption?.value === value
      ? pickedOption
      : selectedUser
      ? toOption(selectedUser)
      : { value, label: `ID: ${value}` }

  return (
    <Select<Option>
      inputId={id}
      options={options}
      value={selectedOption}
      onChange={(option) => {
        setPickedOption(option)
        onChange(option?.value ?? "")
      }}
      // 選択・フォーカスアウトで react-select が空にする分も反映しないと、検索文字が残って選択中の表示が隠れる
      inputValue={inputValue}
      onInputChange={setInputValue}
      // 絞り込みは API の検索に任せる
      filterOption={null}
      isClearable
      isLoading={isLoading}
      placeholder="名前・フリガナ・ID で検索"
      noOptionsMessage={() =>
        inputValue.trim() === ""
          ? "名前・フリガナ・ID を入力してください"
          : "該当するユーザーはいません"
      }
      loadingMessage={() => "検索しています…"}
      // モーダルは overflow で中身を切り取るので、選択肢は body に出す
      menuPortalTarget={typeof document === "undefined" ? null : document.body}
      styles={{
        control: (styles) => ({
          ...styles,
          minHeight: 40,
          borderColor: error ? "var(--color-text-error)" : styles.borderColor,
        }),
        menuPortal: (styles) => ({ ...styles, zIndex: 60 }),
      }}
    />
  )
}
